import { GeneralResourceDetail, TELEPHONE_NUMBER_REGEX } from '@cibo/core'
import { Loading } from '@cibo/profile/src/components'
import { useAuth, useUpdateUserDetails, useUserDetails } from '@cibo/profile/src/hooks'
import { useOrgUser } from '@cibo/profile/src/queries'
import { ClearOrSaveButtonsFormik, ResourceDetailFeatureTaskEditorProps, TextField } from '@cibo/ui'
import {
  Button,
  Collapse,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { mergeAll } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../../queries'
import { filterEmptyStrings } from './filterEmptyStrings'
import { ApplicationUsdaGrowerDetails, UsdaGrowerDetails, UsdaGrowerDetailsValues } from './types'
import { DEFAULT_MAILING_ADDRESS, UsdaMailingAddressField } from './UsdaMailingAddressField'

/**
 * Assumptions:
 * 1. you will only pass a single resourceId in the resourceIds array
 * 2. there will only ever be a single detail for a given resourceId
 */
export const UsdaGrowerDetailsTaskEditorForm = ({
  detailRequirements,
  resourceIds: [resourceId],
  engagementId,
  onClear,
  nameOnly,
  onSubmit,
}: ResourceDetailFeatureTaskEditorProps<any> & {
  onClear(setSubmitting: (isSubmitting: boolean) => void): () => void
  nameOnly?: boolean
  onSubmit(values: any, { setSubmitting }: any): void
}) => {
  const { t } = useTranslation('@cibo/UsdaGrowerDetailsTaskEditor')
  const details = useProgramEngagementDetails({
    detailRequirements,
    resourceId: engagementId,
  })

  const { values, setSubmitting } = useFormikContext<UsdaGrowerDetailsValues>()
  const orgUser = useOrgUser(resourceId)
  const { userId } = useAuth()
  const context = resourceId === userId?.toString() ? 'participant' : 'manager'

  const detail = details.data?.[0]

  const disabled = detail?.immutable || details?.isLoading

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('title')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        <Stack spacing={1}>
          <FormLabel required>{t('fullNameCta', { context })}</FormLabel>

          <FormControl component="fieldset" variant="standard" disabled={disabled}>
            <Stack direction="column" alignItems="flex-start" spacing={1}>
              <Stack direction="row" spacing={2} style={{ width: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  <Field
                    id="firstName"
                    name="firstName"
                    component={TextField}
                    InputProps={{
                      inputProps: {
                        'data-testid': 'firstName',
                        autoComplete: 'section-basics given-name',
                      },
                    }}
                    maxLength={255}
                    placeholder={t('firstName')}
                    disabled={disabled}
                    fullWidth={true}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Field
                    id="lastName"
                    name="lastName"
                    component={TextField}
                    InputProps={{
                      inputProps: {
                        'data-testid': 'lastName',
                        autoComplete: 'section-basics family-name',
                      },
                    }}
                    maxLength={255}
                    placeholder={t('lastName')}
                    disabled={disabled}
                    fullWidth={true}
                  />
                </Box>
              </Stack>
              {values.firstName === '' && values.lastName === '' && orgUser.data?.user && (
                <Button
                  onClick={() => {
                    onSubmit(
                      {
                        firstName: orgUser.data?.user?.givenName || '',
                        lastName: orgUser.data?.user?.familyName || '',
                      },
                      { setSubmitting }
                    )
                  }}
                  variant="text"
                  color="secondary"
                  sx={{ paddingBlock: 0.25 }}
                >
                  {t('useAccountName', {
                    name: `${orgUser.data?.user?.givenName} ${orgUser.data?.user?.familyName}`,
                  })}
                </Button>
              )}
            </Stack>
          </FormControl>
        </Stack>

        {!nameOnly && (
          <Stack spacing={1}>
            <FormLabel required>{t('telephoneNumberCta', { context })}</FormLabel>

            <FormControl component="fieldset" variant="standard" disabled={disabled}>
              <Stack direction="row" spacing={2} style={{ width: '100%' }}>
                <Stack spacing={1} style={{ flex: 1 }}>
                  <FormLabel>{t('mobile')}</FormLabel>
                  <Field
                    id="mobilePhone"
                    name="mobilePhone"
                    component={TextField}
                    InputProps={{
                      inputProps: {
                        'data-testid': 'mobilePhone',
                        autoComplete: 'section-basics tel mobile',
                      },
                    }}
                    maxLength={255}
                    placeholder={t('phoneNumber')}
                    disabled={disabled}
                    fullWidth={true}
                  />
                </Stack>
                <Stack spacing={1} style={{ flex: 1 }}>
                  <Typography>{t('business')}</Typography>
                  <Field
                    id="businessPhone"
                    name="businessPhone"
                    component={TextField}
                    InputProps={{
                      inputProps: {
                        'data-testid': 'businessPhone',
                        autoComplete: 'section-basics tel work',
                      },
                    }}
                    maxLength={255}
                    placeholder={t('phoneNumber')}
                    disabled={disabled}
                    fullWidth={true}
                  />
                </Stack>
              </Stack>
            </FormControl>
          </Stack>
        )}

        {!nameOnly && (
          <Stack>
            <Stack>
              <Typography>{t('mailingAddress')}</Typography>
              <Typography variant="caption">{t('mailingAddressDescription')}</Typography>
            </Stack>

            <UsdaMailingAddressField name="mailingAddress" />
          </Stack>
        )}

        <ClearOrSaveButtonsFormik
          hasValidDetail={!!detail?.value}
          disabled={disabled}
          onClear={onClear(setSubmitting)}
          testid="growerDetails"
        />
      </Stack>
    </Form>
  )
}
export const UsdaGrowerDetailsTaskEditor = <
  T extends UsdaGrowerDetails | ApplicationUsdaGrowerDetails = UsdaGrowerDetails
>(
  props: ResourceDetailFeatureTaskEditorProps<GeneralResourceDetail<T>> & {
    nameOnly?: boolean
  }
) => {
  const { workflowId, engagementId } = useParams()
  const { t } = useTranslation('@cibo/UsdaGrowerDetailsTaskEditor')
  const { nameOnly, onUpdating, onError, onSuccess, detailRequirements } = props
  const resourceId = props.resourceIds[0]
  const dataType = detailRequirements[0]?.dataType

  // // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const userDetails = useUserDetails({
    resourceId,
    detailRequirements: props.detailRequirements,
  })

  const engagementDetails = useProgramEngagementDetails({
    resourceId: workflowId || engagementId,
    detailRequirements: props.detailRequirements,
  })

  const updateUserDetails = useUpdateUserDetails<UsdaGrowerDetails>()
  const updateEngagementDetails = useUpdateProgramEngagementDetails<
    GeneralResourceDetail<ApplicationUsdaGrowerDetails>
  >({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    engagementId: workflowId || engagementId!,
  })

  const detail = dataType === 'workflow' ? engagementDetails?.data?.[0] : userDetails?.data?.[0]

  const DEFAULT_FORM_VALUES = {
    firstName: '',
    lastName: '',
    mobilePhone: '',
    businessPhone: '',
    mailingAddress: DEFAULT_MAILING_ADDRESS,
  }

  const schema = useMemo(
    () =>
      yup
        .object({
          firstName: yup.string().min(2).required(t('required')),
          lastName: yup.string().min(2).required(t('required')),

          businessPhone: nameOnly
            ? yup.string().nullable()
            : yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),
          mobilePhone: nameOnly
            ? yup.string().nullable()
            : yup.string().matches(TELEPHONE_NUMBER_REGEX, t('needsValidPhoneNumber')),

          mailingAddress: nameOnly ? yup.object().nullable() : yup.object(),
        })
        .test('phoneNumber', t('phoneRequired'), function (value) {
          const phoneNumberPresent = !!value?.businessPhone || !!value?.mobilePhone

          if (!phoneNumberPresent && !nameOnly) {
            return this.createError({
              path: 'mobilePhone',
              message: t('phoneRequired'),
            })
          }

          return true
        }),
    [t]
  )

  const year = detailRequirements[0]?.year

  const onSubmit = useCallback(
    (values: any | undefined, { setSubmitting }: any) => {
      const filtered: any = filterEmptyStrings(values as object)
      const value = values === undefined ? undefined : { ...filtered }

      onUpdating?.()
      if (dataType === 'workflow') {
        updateEngagementDetails
          .mutateAsync({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            resourceId: workflowId || engagementId!,
            details: [
              {
                traitId: props.detailRequirements[0]?.traitId || 'applicationUsdaGrowerDetails',
                value,
              } as GeneralResourceDetail<ApplicationUsdaGrowerDetails>,
            ],
          })
          .then(onSuccess)
          .catch(onError)
          .finally(() => setSubmitting(false))
      } else {
        updateUserDetails
          .mutateAsync({
            resourceId,
            details: [
              {
                traitId: props.detailRequirements[0]?.traitId || 'usdaGrowerDetails',
                year,
                value,
              } as UsdaGrowerDetails,
            ],
          })
          .then(onSuccess)
          .catch(onError)
          .finally(() => setSubmitting(false))
      }
    },
    [onUpdating, onError, onSuccess, resourceId, year]
  )

  const onClear = useCallback(
    (setSubmitting: (isSubmitting: boolean) => void) => {
      return () => onSubmit(undefined, { setSubmitting })
    },
    [onSubmit]
  )

  if (userDetails.isLoading || engagementDetails.isLoading) return <Loading />

  const initialValues = mergeAll([DEFAULT_FORM_VALUES, detail?.value || {}])

  return (
    <Stack spacing={2}>
      <Formik<UsdaGrowerDetailsValues>
        // @ts-ignore How to allow empty strings for these initial values?
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={schema}
        validateOnMount
        enableReinitialize
      >
        <UsdaGrowerDetailsTaskEditorForm
          {...props}
          detailRequirements={props.detailRequirements}
          nameOnly={nameOnly}
          onClear={onClear}
          onSubmit={onSubmit}
        />
      </Formik>
      <Collapse in={!!updateUserDetails.isError}>
        <FormHelperText>{updateUserDetails.error?.response?.data?.message}</FormHelperText>
      </Collapse>
    </Stack>
  )
}
